<script lang="ts" setup>
const props = defineProps({
  size: {
    default: 'md',
    type: String as PropType<UInputSize>,
  },
  webhook: {
    required: true,
    type: Object as PropType<Webhook>,
  },
})

const { createToast } = useToasts()
const { t } = useI18n()
const listsStore = useListsStore()
const localePath = useLocalePath()
const teamsStore = useTeamsStore()

const editing = ref(false)
const updatingWebhook = ref(false)

const options = computed((): USelectMenuOption[] => {
  return listsStore.lists.map((list) => {
    return {
      chip: (list.unseenActions || []).reduce(
        (acc, action) => acc + (action?.data?.domains?.length || 1),
        0,
      ),
      icon: getIcon('list'),
      id: list.id,
      label: list.name,
      value: localePath({
        name: 'companies-lists-listId',
        params: { listId: `${list.id}` },
      }),
    }
  })
})

const selectedLists = computed<USelectMenuOption[]>(
  {
    get: () => {
      return (
        (props?.webhook?.lists || [])
          .map(list => options.value.find(option => option.id === list.id))
          .filter(Boolean) as USelectMenuOption[]
      )
    },
    set: async (value) => {
      updatingWebhook.value = true

      if (value.length > (props?.webhook?.lists?.length || 0)) {
        // Attaching a list
        try {
          await teamsStore.updateTeamWebhook({
            listsToAdd: value.map(list => list.id),
            webhookId: props.webhook.id,
          })
        }
        catch (error) {
          console.error(error)
        }
      }
      else {
        const newListIds = value.map(list => list.id)

        // Detaching a list
        try {
          await teamsStore.updateTeamWebhook({
            listsToRemove: props?.webhook?.lists?.map(list => list.id).filter(listId => !newListIds.includes(listId)) || [],
            webhookId: props.webhook.id,
          })
        }
        catch (error) {
          console.error(error)
        }
      }

      await listsStore.fetchLists({
        page: 1,
        size: 500,
      })

      createToast({ message: 'teamWebhookUpdated' })

      updatingWebhook.value = false
    },
  },
)
</script>

<template>
  <USelectMenu
    v-if="!editing"
    v-model="selectedLists"
    class="w-52"
    :icon="getIcon('list')"
    :loading="updatingWebhook"
    multiple
    :options="options"
    searchable
    :searchable-placeholder="t('searchYourLists')"
    :size="size"
    :ui-menu="{ container: 'min-w-48' }"
  >
    <template #label>
      <span class="truncate">
        {{ selectedLists.length ? `${selectedLists.length} ${selectedLists.length > 1 ? t('listsAttached') : t('listAttached')}` : t('attachLists') }}
      </span>
    </template>

    <template #option="{ option }">
      <div class="flex items-center gap-2">
        <UIcon
          class="size-5 flex-shrink-0"
          :name="option.icon"
        />

        <span class="text-sm">
          {{ option.label }}
        </span>

        <UChip
          v-if="option.chip"
          class="ml-1 [&>*]:!text-white"
          color="red"
          size="xl"
          :text="option?.chip > 9 ? '9+' : option.chip"
          variant="subtle"
        />
      </div>
    </template>
  </USelectMenu>
</template>

<i18n lang="json">
{
  "en": {
    "attachLists": "Attach lists",
    "listAttached": "list attached",
    "listsAttached": "lists attached",
    "searchYourLists": "Search your lists"
  },
  "es": {
    "attachLists": "Añadir listas",
    "listAttached": "lista añadida",
    "listsAttached": "listas añadidas",
    "searchYourLists": "Buscar en tus listas"
  },
  "fr": {
    "attachLists": "Attacher des listes",
    "listAttached": "liste attachée",
    "listsAttached": "listes attachées",
    "searchYourLists": "Rechercher dans vos listes"
  }
}
</i18n>
